import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSession } from '@/hooks/useSession';
import { supabase } from '@/lib/supabase';
import type { UserRole } from '@/lib/types';

interface AuthGuardProps {
  children: React.ReactNode;
  /** If set, requires the user to have one of these roles. Default: any authenticated user. */
  requiredRoles?: UserRole[];
}

// Wraps a route. Outcomes:
//   - session loading → skeleton
//   - no session → redirect to /login
//   - session OK but user.deactivated → force sign-out + redirect with reason
//   - session OK but role missing → 'forbidden' card (no redirect / caller
//     knows it landed here intentionally)
export function AuthGuard({ children, requiredRoles }: AuthGuardProps) {
  const { ready, isAuthenticated, role, profile } = useSession();
  const location = useLocation();

  // The auth-level ban (set by the worker's /users/deactivate) blocks NEW
  // sign-ins, but an in-memory JWT keeps working until refresh. So we also
  // gate at the React layer: if the loaded profile says deactivated, we
  // sign out immediately and bounce to /login with a reason.
  useEffect(() => {
    if (ready && isAuthenticated && profile?.deactivated) {
      void supabase.auth.signOut();
    }
  }, [ready, isAuthenticated, profile?.deactivated]);

  if (!ready) {
    return (
      <div className="flex h-screen items-center justify-center bg-bg">
        <div className="text-fg-faint text-sm font-mono">restoring session…</div>
      </div>
    );
  }

  if (isAuthenticated && profile?.deactivated) {
    return <Navigate to="/login?reason=deactivated" replace />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (requiredRoles && requiredRoles.length > 0 && !(role && requiredRoles.includes(role))) {
    return (
      <div className="flex h-screen items-center justify-center bg-bg p-8">
        <div className="max-w-md text-center space-y-2">
          <div className="text-h2 font-editorial">Not authorized</div>
          <p className="text-fg-muted text-sm">
            Your role (<code className="font-mono text-fg">{role ?? 'none'}</code>) doesn't have access to this area.
            Required: <code className="font-mono text-fg">{requiredRoles.join(' / ')}</code>.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
